/**
 * The user guides, as cards.
 *
 * THIS APP SHIPPED NONE. ParkourAI Docs is the most unfamiliar surface in the suite -- it is an
 * editor, and people arrive with Google Docs in their hands -- and it was the only one with
 * nothing to read. Two features that landed this week, tab stops and named versions, are invisible
 * by looking at the screen.
 *
 * AT /help, WHICH WAS A DEAD ROUTE. The account menu has always offered "Help" and it navigated to
 * a path nothing served, so the catch-all redirected to the document list: the menu item did
 * nothing, silently, and looked like a product that had lost its way rather than one missing a
 * page.
 *
 * OPENED IN A POPUP, NOT ROUTED TO. A guide is read ALONGSIDE the thing it describes -- an author
 * reading about tab stops wants the ruler on screen -- so navigating away from the document to
 * read about the document is exactly wrong. The window name is per-guide, so opening the same
 * guide twice reuses its window instead of stacking copies.
 *
 * TITLES AND DESCRIPTIONS ARE THE GUIDES' OWN, taken from each file. A card that describes a guide
 * differently from the guide itself is a second source of truth.
 */
import React from "react";
import { Typography } from "@mui/material";
import {
    DescriptionOutlined, StraightenOutlined, ImageOutlined, GroupsOutlined,
    HistoryOutlined, PictureAsPdfOutlined, RateReviewOutlined, ForumOutlined,
    LinkOutlined,
} from "@mui/icons-material";

import { Blue3, Grey6 } from "../theme/styles";
import CCTileGrid, { Tile } from "../components/Common/CCTileGrid";

type Guide = {
    icon: React.ReactNode;
    title: string;
    description: string;
    file: string;
    windowName: string;
};

const ART = { color: Blue3, width: "75px", height: "75px" };

const GUIDES: Guide[] = [
    {icon: <DescriptionOutlined sx={ART}/>, title: "Writing a controlled document",
     file: "writing-a-document.html", windowName: "parkourWritingDocs",
     description: "Where the text lives, what decides whether you may type in it, when it saves, and the parts a revision can hold."},
    {icon: <StraightenOutlined sx={ART}/>, title: "Formatting and layout",
     file: "formatting-and-layout.html", windowName: "parkourLayoutDocs",
     description: "The ruler: indents, hanging indents and tab stops. Tables and what a column may hold, headings, footnotes and spacing — and why the PDF matches the screen."},
    {icon: <LinkOutlined sx={ART}/>, title: "Numbered sections and references",
     file: "references.html", windowName: "parkourReferencesDocs",
     description: "Numbering that follows the document's shape, cross-references and bookmarks that cannot go stale, and a contents list with no update button."},
    {icon: <ImageOutlined sx={ART}/>, title: "Figures",
     file: "figures.html", windowName: "parkourFiguresDocs",
     description: "Putting a picture in a procedure: how it is stored, how it is sized, and what everybody else sees."},
    {icon: <GroupsOutlined sx={ART}/>, title: "Working on a document together",
     file: "collaborating.html", windowName: "parkourCollaboratingDocs",
     description: "Two people in one document: whose caret is whose, what happens when you both type, and proposing a change instead of making it."},
    {icon: <RateReviewOutlined sx={ART}/>, title: "Reviewing",
     file: "reviewing.html", windowName: "parkourReviewingDocs",
     description: "Suggesting changes instead of making them: what a proposal looks like, accepting and rejecting, and why they stop an approval."},
    {icon: <ForumOutlined sx={ART}/>, title: "Comments",
     file: "commenting.html", windowName: "parkourCommentingDocs",
     description: "Asking about a procedure without changing it — including while it is out for approval — naming somebody with @, and why both are safe."},
    {icon: <HistoryOutlined sx={ART}/>, title: "Version history",
     file: "version-history.html", windowName: "parkourHistoryDocs",
     description: "Naming the version that went to review, reading any moment back, and what the entries in the list actually mean."},
    {icon: <PictureAsPdfOutlined sx={ART}/>, title: "The PDF, and controlled copies",
     file: "renditions.html", windowName: "parkourRenditionDocs",
     description: "What the rendition carries on every page, when it is regenerated, and what a printed copy is and is not."},
];

export default function Documentation() {
    const tiles: Tile[] = GUIDES.map(guide => ({
        key: guide.file,
        icon: guide.icon,
        label: guide.title,
        description: guide.description,
        actionLabel: "Open guide",
        onAction: () => window.open("/docs/" + guide.file, guide.windowName,
                                    "width=1200,height=900,scrollbars=yes,resizable=yes"),
    }));

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between",
                         height: 52, backgroundColor: "#1498D8"}}>
                <div style={{paddingTop: "9px", paddingLeft: "20px"}}>
                    <Typography style={{fontSize: 24, fontWeight: 400, color: "#FFFFFF"}}>
                        Documentation
                    </Typography>
                </div>
            </div>
            <div style={{padding: "16px 20px 0"}}>
                <Typography style={{fontSize: 13, color: Grey6}}>
                    Every guide this app ships. Each opens in its own window, so you can read it
                    beside the document you are working on — and print it.
                </Typography>
            </div>
            <CCTileGrid tiles={tiles}/>
        </div>
    );
}
